import type { LEVERANDOR_REGIONS, COUNTRY_MENU_OPTION } from '../types';
import {
    LEVERANDOR_CULTURE_CODES,
    LEVERANDOR_REGISTRERING_STATUS,
    LEVERANDOR_REGIONS as LEVERANDOR_REGIONS_ENUM,
    COUNTRY_WITH_API_SUPPORT,
    COUNTRIES_WITH_API_SUPPORT_LIST,
} from '../types';
import {
    localStorageReferences,
    leverandorSelectorActionsReferences,
} from '../store/leverandorSlice';
import type { BransjeRisiko } from '@klp/formgenerator/services/rtkApi';

export const mapRegionToCountryCode = (
    region?: LEVERANDOR_REGIONS | string,
): LEVERANDOR_CULTURE_CODES => {
    if (!region) return LEVERANDOR_CULTURE_CODES.Norwegian;

    const regionMap: Record<LEVERANDOR_REGIONS, LEVERANDOR_CULTURE_CODES> = {
        Oslo: LEVERANDOR_CULTURE_CODES.Norwegian,
        Trondheim: LEVERANDOR_CULTURE_CODES.Norwegian,
        Sverige: LEVERANDOR_CULTURE_CODES.Swedish,
        Danmark: LEVERANDOR_CULTURE_CODES.Danish,
        Europa: LEVERANDOR_CULTURE_CODES.English,
    };

    return regionMap[region] || LEVERANDOR_CULTURE_CODES.Norwegian;
};

/**
 * Map users region value to organization country for Company Select component
 */
export const mapRegionToCountryOption = (
    region: LEVERANDOR_REGIONS | undefined,
): string | undefined => {
    if (!region) return undefined;

    const regionToCountryMap: Record<LEVERANDOR_REGIONS, COUNTRY_MENU_OPTION> =
        {
            [LEVERANDOR_REGIONS_ENUM.Oslo]: COUNTRY_WITH_API_SUPPORT.Norway,
            [LEVERANDOR_REGIONS_ENUM.Trondheim]:
                COUNTRY_WITH_API_SUPPORT.Norway,
            [LEVERANDOR_REGIONS_ENUM.Sverige]: COUNTRY_WITH_API_SUPPORT.Sweden,
            [LEVERANDOR_REGIONS_ENUM.Danmark]: COUNTRY_WITH_API_SUPPORT.Denmark,
            [LEVERANDOR_REGIONS_ENUM.Europa]:
                COUNTRY_WITH_API_SUPPORT.UnitedKingdom,
        };

    return regionToCountryMap[region];
};

/**
 * Map organization country to the appropriate region value for API lookups
 */
export const mapOrganizationCountryToRegion = (
    country: COUNTRY_WITH_API_SUPPORT | undefined,
): LEVERANDOR_REGIONS => {
    if (!country) return LEVERANDOR_REGIONS_ENUM.Oslo;

    const countryToRegionMap: Record<
        COUNTRY_WITH_API_SUPPORT,
        LEVERANDOR_REGIONS
    > = {
        [COUNTRY_WITH_API_SUPPORT.Norway]: LEVERANDOR_REGIONS_ENUM.Oslo,
        [COUNTRY_WITH_API_SUPPORT.Sweden]: LEVERANDOR_REGIONS_ENUM.Sverige,
        [COUNTRY_WITH_API_SUPPORT.Denmark]: LEVERANDOR_REGIONS_ENUM.Danmark,
        [COUNTRY_WITH_API_SUPPORT.UnitedKingdom]:
            LEVERANDOR_REGIONS_ENUM.Europa,
    };

    return countryToRegionMap[country];
};

/**
 * Map country to flag/
 */
export const mapCountryToFlag = (
    country: COUNTRY_MENU_OPTION | undefined,
): string | undefined => {
    if (!country) return undefined;

    const countryToFlagMap: Record<COUNTRY_MENU_OPTION, string> = {
        [COUNTRY_WITH_API_SUPPORT.Norway]: '🇳🇴',
        [COUNTRY_WITH_API_SUPPORT.Sweden]: '🇸🇪',
        [COUNTRY_WITH_API_SUPPORT.Denmark]: '🇩🇰',
        [COUNTRY_WITH_API_SUPPORT.UnitedKingdom]: '🇬🇧',
        ['Other']: '🏳️',
    };

    return countryToFlagMap[country];
};

export const isCountryWithApiSupport = (
    country: string | undefined,
): country is COUNTRY_WITH_API_SUPPORT => {
    if (!country) return false;

    return COUNTRIES_WITH_API_SUPPORT_LIST.includes(country);
};

/**
 * Get the i18n translation key for a leverandor status
 */
const getLeverandorStatusLabel = (
    status?: LEVERANDOR_REGISTRERING_STATUS | string,
): string => {
    switch (status) {
        case LEVERANDOR_REGISTRERING_STATUS.UNDER_VURDERING:
            return 'leverandor.statuses.UnderVurdering';
        case LEVERANDOR_REGISTRERING_STATUS.FULLFORT:
            return 'leverandor.statuses.Fullfort';
        case LEVERANDOR_REGISTRERING_STATUS.AVVIST:
            return 'leverandor.statuses.Avvist';
        default:
            return 'leverandor.statuses.unknown';
    }
};

/**
 * Map leverandor registrering status to display status with translations
 * Ny -> Under Godkjenning
 * Fullfort -> Godkjent
 * Avvist -> Ikke Godkjent
 */
const mapLeverandorStatusToDisplayStatus = (
    status?: LEVERANDOR_REGISTRERING_STATUS | string,
): string => {
    switch (status) {
        case LEVERANDOR_REGISTRERING_STATUS.UNDER_VURDERING:
            return 'leverandor.displayStatuses.underGodkjenning';
        case LEVERANDOR_REGISTRERING_STATUS.FULLFORT:
            return 'leverandor.displayStatuses.godkjent';
        case LEVERANDOR_REGISTRERING_STATUS.AVVIST:
            return 'leverandor.displayStatuses.ikkeGodkjent';
        default:
            return 'leverandor.statuses.unknown';
    }
};

const getLeverandorStatusValues = (): Array<string> => {
    return Object.values(LEVERANDOR_REGISTRERING_STATUS).filter(
        (value) => typeof value === 'string',
    ) as Array<string>;
};

const getLeverandorStatusKeys = (): Array<string> => {
    return Object.keys(LEVERANDOR_REGISTRERING_STATUS).filter(
        (key) => isNaN(Number(key)), // Filter out numeric keys from enum reverse mapping
    );
};

const sortBransjeListByValue = (
    bransjeList: Record<string, BransjeRisiko>,
): Array<BransjeRisiko> => {
    const values = Object.values(bransjeList);
    return values.sort((a, b) => {
        const valueA = a.navn;
        const valueB = b.navn;
        return valueA.localeCompare(valueB);
    });
};

export {
    getLeverandorStatusLabel,
    mapLeverandorStatusToDisplayStatus,
    localStorageReferences,
    leverandorSelectorActionsReferences,
    getLeverandorStatusValues,
    getLeverandorStatusKeys,
    sortBransjeListByValue,
};
